import { DataTable } from 'components/DataTable/DataTable';
import { ControlledTextField } from 'components/Form';
import type { ICollapseFieldsToCheckItem } from 'components/Form/ControlledCollapseGroup';
import { ControlledCollapseGroup } from 'components/Form/ControlledCollapseGroup';
import { ReadField } from 'components/ReadField';
import type { ContractSeasonData, ContractSeasonInflationData } from 'gql/graphql';
import type { ParsedContractData } from 'pages/Project/UerProject/Contract/utils';
import { GridItem, Card, Button, CollapseGroup } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import * as R from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useConversionIndicesColumns } from './hooks/useConversionIndicesColumns';
import { useSeasonsInflationColumns } from './hooks/useSeasonsInflationColumns';
import { ParametersPriceOptions } from './ParametersPriceOptions/ParametersPriceOptions';
import { SeasonsSelectModal } from './SeasonsSelectModal/SeasonsSelectModal';

export interface IItems {
    label: string;
    content: JSX.Element;
}

interface IPriceOptions {
    control?: Control;
    isActive?: boolean;
    defaultValues: ParsedContractData;
    isUpdateForm?: boolean;
    makeContractDataRequest?: any;
    fieldsToCheck?: Array<ICollapseFieldsToCheckItem>;
    classNameTooltip?: string;
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
}

export const getInflationBySeasons = (seasons?: ContractSeasonData[] | null): ContractSeasonInflationData[] =>
    seasons?.map((season) => ({ id: season.id, inflationRate: 0, year: season.year })) || [];

export const PriceOptions = (props: IPriceOptions) => {
    const dispatch = useDispatch();
    const {
        control,
        defaultValues,
        isActive,
        isUpdateForm,
        makeContractDataRequest,
        fieldsToCheck,
        classNameTooltip,
        onTooltipAttributeChange,
    } = props;
    const { isEditablePriceParams, id: contractId, seasons, seasonsInflation, workOrderNavs } = defaultValues;
    const isEditable = useMemo(
        () => isUpdateForm && !workOrderNavs?.length && isEditablePriceParams,
        [isEditablePriceParams, isUpdateForm, workOrderNavs?.length],
    );
    const isSupplyBasisEnabled = useMemo(
        () => isUpdateForm && !workOrderNavs?.length && !defaultValues.hasSupplyBasisInContract,
        [defaultValues.hasSupplyBasisInContract, isUpdateForm, workOrderNavs?.length],
    );

    const conversionIndicesColumns = useConversionIndicesColumns({
        control,
        isEditable,
    });
    const seasonColumns = useSeasonsInflationColumns({ control, isEditable });

    const hasToLoadSeasons = useMemo(() => !seasons?.length, [seasons?.length]);

    const sortedSeasonsInflation = useMemo(() => {
        // @ts-ignore
        const byYear = R.ascend(R.prop('year'));

        // @ts-ignore
        return R.sort(byYear, seasonsInflation || getInflationBySeasons(seasons));
    }, [seasons, seasonsInflation]);

    const [isSeasonSelectorOpen, setIsSeasonSelectorOpen] = useState(false);

    const items: IItems[] = useMemo(
        () => [
            {
                label: 'Индексы перевода в текущий уровень цен',
                content: (
                    <DataTable
                        borderBetweenRows
                        outerBorders
                        columns={conversionIndicesColumns}
                        data={defaultValues?.conversionIndices || []}
                        verticalAlign="center"
                    />
                ),
            },
            {
                label: 'Сезон',
                content: (
                    <DataTable
                        borderBetweenRows
                        outerBorders
                        columns={seasonColumns}
                        data={sortedSeasonsInflation as any}
                        verticalAlign="center"
                    />
                ),
            },
            {
                label: 'Параметры',
                content: (
                    <ParametersPriceOptions
                        params={defaultValues?.constructionTypeAttributes}
                        disabled={!isEditable}
                        isUpdateForm={isUpdateForm}
                        control={control}
                    />
                ),
            },
        ],
        [
            conversionIndicesColumns,
            defaultValues?.conversionIndices,
            defaultValues?.constructionTypeAttributes,
            seasonColumns,
            sortedSeasonsInflation,
            isEditable,
            isUpdateForm,
            control,
        ],
    );

    const onSeasonSelectClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsSeasonSelectorOpen(true);
    }, []);

    const onSeasonSelectorClose = useCallback(() => {
        setIsSeasonSelectorOpen(false);
    }, []);

    const onSeasonSelectorSuccess = useCallback(() => {
        dispatch(makeContractDataRequest({ contractId }));
        onSeasonSelectorClose();
    }, [contractId, dispatch, makeContractDataRequest, onSeasonSelectorClose]);

    const opened = useMemo(() => items.map((_, index) => index), [items]);

    return (
        <div
            style={{
                margin: 'var(--space-margin-bottom)',
                marginLeft: 0,
                maxWidth: isUpdateForm && isEditable ? 768 : 680,
            }}
        >
            {hasToLoadSeasons && (
                <Card
                    style={{
                        padding: 16,
                        width: 675,
                        background: 'var(--color-bg-stripe)',
                        fontWeight: 600,
                        display: 'flex ',
                        alignItems: 'center',
                    }}
                >
                    Для отображения данных выберите сезоны{' '}
                    <Button label="Выбрать" onClick={onSeasonSelectClick} style={{ marginLeft: 'auto' }} />
                </Card>
            )}
            {isSeasonSelectorOpen && (
                <SeasonsSelectModal
                    onClose={onSeasonSelectorClose}
                    onSuccess={onSeasonSelectorSuccess}
                    contractId={contractId}
                />
            )}
            {!hasToLoadSeasons && (
                <>
                    <GridItem style={{ maxWidth: '496px' }}>
                        {isUpdateForm ? (
                            <ControlledTextField
                                name="supplyBasis"
                                type="text"
                                label="Базис поставки"
                                labelPosition="left"
                                control={control}
                                disabled={!isSupplyBasisEnabled}
                                required
                                rules={requiredBaseRule}
                                classNameTooltip={!isActive ? 'hidden-tooltip' : classNameTooltip}
                                style={{ width: 675 }}
                            />
                        ) : (
                            <ReadField label="Базис поставки" value={defaultValues?.supplyBasis} />
                        )}
                    </GridItem>
                    <GridItem>
                        {control ? (
                            <ControlledCollapseGroup
                                items={items}
                                control={control}
                                fieldsToCheck={fieldsToCheck}
                                classNameTooltip={classNameTooltip}
                                onTooltipAttributeChange={onTooltipAttributeChange}
                                labelWithLessWidth
                            />
                        ) : (
                            <CollapseGroup items={items} opened={opened} />
                        )}
                    </GridItem>
                </>
            )}
        </div>
    );
};

PriceOptions.displayName = 'PriceOptions';
